'use client';

import { FileTextIcon } from '@/components/icons';
import { LegalPageLayout } from '@/components/legal/legal-page-layout';

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="space-y-1.5 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1 h-1 rounded-pill bg-[var(--primary)] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <div className="p-4 rounded-card bg-[var(--primary-subtle)] text-[var(--text-primary)] text-sm">
    {children}
  </div>
);

export function OfertaContent() {
  const sections = [
    {
      title: '1. Предмет оферты',
      content: <p>Продавец обязуется передать Покупателю, а Покупатель оплачивает и принимает заказанную продукцию (Продукция).</p>,
    },
    {
      title: '2. Заключение договора',
      content: (
        <>
          <Highlight>
            <p>Данный договор — это публичная оферта в соответствии со ст. 435 и ч. 2 ст. 437 ГК РФ.</p>
          </Highlight>
          <p>Оформление заказа на Продукцию является принятием условий договора. При этом Покупатель вступает в договорные отношения с продавцом.</p>
        </>
      ),
    },
    {
      title: '3. Цена Продукции',
      content: (
        <BulletList items={[
          'Цены на сайте указаны в валюте страны Покупателя за единицу Продукции',
          'Тарифы на услуги доставки определяются отдельно на каждый вид Продукции',
          'Итоговая сумма заказа указана в разделе «Корзина» в строке «Итого»',
        ]} />
      ),
    },
    {
      title: '4. Оплата Продукции',
      content: (
        <BulletList items={[
          'При наличном расчёте оплата производится в момент передачи Продукции',
          'При безналичном расчёте оплата считается выполненной при списании средств',
          'Продукция поставляется в объёме, соответствующем произведённой оплате',
        ]} />
      ),
    },
    {
      title: '5. Доставка Продукции',
      content: (
        <BulletList items={[
          'Доставка осуществляется в оговорённые сроки по указанному адресу',
          'Стоимость указывается при оформлении и не может быть изменена после начала выполнения',
        ]} />
      ),
    },
    {
      title: '6. Гарантия на Продукцию',
      content: (
        <Highlight>
          <p>Вся Продукция имеет сертификаты качества и заключения санитарно-эпидемиологической службы.</p>
        </Highlight>
      ),
    },
    {
      title: '7. Права и обязанности сторон',
      content: (
        <>
          <p className="font-medium text-[var(--text-primary)]">Продавец обязан:</p>
          <BulletList items={[
            'Передать информацию о Продукции до заключения договора',
            'Не разглашать личные данные Покупателя',
          ]} />
          <p className="font-medium text-[var(--text-primary)] mt-4">Покупатель обязан:</p>
          <BulletList items={[
            'Ознакомиться с договором-офертой до заказа',
            'Предоставить достоверную информацию для доставки',
            'Оплатить Продукцию в указанные сроки',
          ]} />
        </>
      ),
    },
    {
      title: '8. Ответственность и решение споров',
      content: (
        <BulletList items={[
          'Стороны несут ответственность согласно договору и законодательству РФ',
          'Все споры решаются путём переговоров или в судебном порядке',
        ]} />
      ),
    },
    {
      title: '9. Возврат и обмен Продукции',
      content: (
        <>
          <Highlight>
            <p>При получении Покупатель проверяет соответствие Продукции заказу. В случае несоответствия можно отказаться от принятия в момент получения.</p>
          </Highlight>
          <p>Уведомление о несоответствии: <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">8 (863) 333-50-50</a></p>
        </>
      ),
    },
    {
      title: '10. Срок действия договора',
      content: <p>Договор вступает в силу с момента оформления заказа и действует до выполнения обязательств сторон.</p>,
    },
    {
      title: '11. Личные данные',
      content: (
        <>
          <p>Продавец получает и обрабатывает данные для:</p>
          <BulletList items={[
            'Выполнения условий договора',
            'Доставки Продукции',
          ]} />
        </>
      ),
    },
    {
      title: '12. Порядок оплаты',
      content: (
        <>
          <p>Оплата возможна банковской картой онлайн сразу после создания заказа.</p>
          <Highlight>
            <p>Оплата производится на защищённой странице процессинговой компании ООО «Нэт Пэй» с соблюдением всех требований безопасности Visa и MasterCard.</p>
          </Highlight>
        </>
      ),
    },
    {
      title: '13. Порядок возврата денежных средств',
      content: (
        <>
          <p>Для возврата средств обратитесь к менеджеру интернет-магазина.</p>
          <p>Возврат производится на карту в течение 2–3 дней (максимальный срок — 30 дней).</p>
          <Highlight>
            <p>Безопасность платежей обеспечивается технологиями Visa и MasterCard с использованием протоколов SSL.</p>
          </Highlight>
        </>
      ),
    },
  ];

  return (
    <LegalPageLayout
      title="Публичная оферта"
      icon={<FileTextIcon className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />}
      sections={sections}
      breadcrumbs={[
        { name: 'Главная', href: '/' },
        { name: 'Публичная оферта', href: '/oferta' },
      ]}
      contactBlock={
        <div className="text-sm text-[var(--text-secondary)]">
          <p>Вопросы по условиям оферты:</p>
          <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">8 (863) 333-50-50</a>
        </div>
      }
    />
  );
}
